import React, { useEffect, useState, useRef } from 'react'
import Medias from './components/Medias'
import Usage from './components/Usage'
import Distribution from './components/Distribution'
import Services from './components/Services'
import Delivery from './components/Delivery'
import Drafts from './components/Drafts'
import RatesCalculator from './components/RatesCalculator'
import Files from 'components/Files'
import {FormattedMessage, useIntl} from "react-intl";
import messages from "./messages";
import useLocalStorageState from 'hooks/useLocalStorageState'
import { Button, LoadingFull } from '@arnsbogroup/react-voicearchive'

const EmbeddedDealPage = ({ match, location }) => {
  const [state, setState] = useState({})
  const [initialState, setInitialState] = useState({})
  const [showDistribution, setShowDistribution] = useLocalStorageState(false, 'EmbeddedDealPage.distribution')
  const [showDrafts, setShowDrafts] = useLocalStorageState(true, 'EmbeddedDealPage.drafts')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const { formatMessage } = useIntl()
  const dealId = location.pathname.match(/\/embedded\/deal\/(\d+)/)[1]

  const callbackRef = useRef((event) => {
    // No-op (until redefined by children components)
  })

  const setCallback = (callback) => {
    callbackRef.current = callback
  }

  useEffect(() => {
    fetch(`/embedded/deal/${dealId}/order`) 
      .then((response) => response.json())
      .then(({ data: { uid } }) => {
        setState({ uid })
        return fetch(`/rest/v1/order/${uid}?includeRelations=1&includeCompanyCurrency=1&includeCompanyPoRequired=1&includeTaskTree=1`)
      })
      .then(response => response.json())
      .then((order) => ({
        ...state,
        ...order,
        priority: order.priority,
        wordCount: order.word_count,
        playOutMinutes: order.play_out_minutes,
        handlingLanguage: order.language_uid,
        usage: order.orderUsageType == [] ? null : { value: order.orderUsageType.uid, label: order.orderUsageType.name },
        deadline: parseInt(order.deadline) > 0 ? new Date(parseInt(order.deadline) * 1000) : null,
        expected: parseInt(order.expected) > 0 ? new Date(parseInt(order.expected) * 1000) : null,
        start: parseInt(order.orderusage_start) > 0 ? new Date(parseInt(order.orderusage_start) * 1000) : null,
        languagesCount: order.language_count || 0,
        medias: order.mediaTypes.map(({ uid, name }) => ({ value: uid, label: name })),
        services: order.services.map(({ uid, name }) => ({ value: uid, label: name })),
        countries: order.countries.map(({ uid, name }) => ({ value: uid, label: name })),
        suppliersCount: order.supplier_count || 0,
        comments: order.comments,
        complex: Boolean(Number(order.complex)),
        companyPoRequired: Boolean(Number(order.companyPoRequired))
      }))
      .then((nextState) => {
        setState(nextState)
        setInitialState(nextState)
        setLoading(false)
      })
      .catch((err) => {
        window.alert(`${formatMessage(messages.error)}: ${err}`);
      })

    if (typeof BX24 === 'undefined' || !BX24) {
      return
    }

    BX24.callMethod('user.current', {}, function(res) {
      console.log(res.data()) // Kept to inspect emdbedded app i Bitrix24 env
    })
  }, [])

  useEffect(() => {
    let interval
    if (state && initialState && state?.uid) {
      interval = setInterval(() => {
        fetch(`/rest/v1/order/${state.uid}`)
          .then((response) => response.json())
          .then((response) => {
            if (response?.tstamp > initialState.tstamp) {
              clearInterval(interval)
              if (confirm("The version of the deal that you are working on is outdated. Do you want to refresh?")) {
                window.location.reload()
              }
            }
          })
          .catch((error) => {
            console.error("Error fetching order data:", error)
          })
      }, 5000)
    }

    return () => {
      clearInterval(interval)
    }
  }, [initialState])
  

  const onSave = () => {
    setSaving(true)
    fetch(`/rest/v1/order/${state.uid}`, {
      method: 'put',
      body: JSON.stringify({
        comments: state.comments,
        complex: state.complex ? '1' : '0',
        countries: state.countries ? state.countries.map(({ value }) => value) : [],
        customerReference: state.customerReference,
        distribution_comments: state.distribution_comments,
        deadline: Date.parse(state.deadline) / 1000,
        expected: Date.parse(state.expected) / 1000,
        language_count: state.languagesCount,
        language_uid: state.handlingLanguage,
        medias: state.medias ? state.medias.map(({ value }) => value) : [],
        orderusage: state.usage ? state.usage.value : null,
        orderusage_start: Date.parse(state.start) / 1000,
        play_out_minutes: state.playOutMinutes,
        priority: state.priority,
        services: state.services ? state.services.map(({ value }) => value) : [],
        supplier_count: state.suppliersCount,
        word_count: state.wordCount,
        coordinator: state.coordinator,
      })
    })
    .then((response) => response.json())
    .then((response) => {
      setSaving(false)
      setInitialState({...state, tstamp: response.tstamp})

      if (callbackRef.current) {
        callbackRef.current()
      }
    }).catch((error) => {
      window.alert(`${formatMessage(messages.error)}: ${error}`);
    })
  }

  const color = Boolean(Number(state.hidden)) ? '#7ed328' : Boolean(Number(state.closed)) ? '#ef491e' : '#3bc8f5'

  if (loading) {
    return <LoadingFull />
  }

  return (
    <div className="flex h-full">
      <div className="px-6 pt-2 border-t-4 max-h-full overflow-scroll w-4/5" style={{ borderTopColor: color }}>
        <div className="flex justify-between">
          <div>
            { saving ? (
              <Button 
                className="text-white uppercase"
                style={{ background: color, borderColor: color }}
                icon={['fa', 'sync']}
              >
                  <FormattedMessage {...messages.saving}/>
              </Button>
            ) : (
              <Button 
                className="text-white uppercase"
                style={{ background: color, borderColor: color }} 
                onClick={onSave}
                icon={['fa', 'save']}
              >
                <FormattedMessage {...messages.save}/>
              </Button>
            ) }
            { JSON.stringify(initialState) !== JSON.stringify(state) &&
              <Button 
                className="uppercase" 
                onClick={() => setState(initialState)}
              >
                <FormattedMessage {...messages.cancel}/>
              </Button>
            }
          </div>

          <div className="mt-2 ml-4 font-medium uppercase mt-4" style={{ color }}>
            <span>
              { Boolean(Number(state.hidden)) ?
                  formatMessage(messages.dealInProgress) :
                  Boolean(Number(state.closed)) ?
                  formatMessage(messages.closed) :
                  formatMessage(messages.orderInProgress)
              }
            </span>

            { state.uid && !Boolean(Number(state.hidden)) &&
              <a target="_blank" href={`https://system.voicearchive.com/orders/${state.uid}`} className="px-6 py-3 text-blue-700 font-medium uppercase ml-3">
                <FormattedMessage {...messages.openOrder} values={{ id: state.uid }} /> <i className="fa fa-external-link" /> 
              </a>
            }
          </div>
        </div>

        <Delivery state={state} setState={setState} />

        <div className="flex w-full space-x-6">
          <div className="w-1/3">
            <Services state={state} setState={setState} />
          </div>
          <div className="w-1/3">
            <Medias state={state} setState={setState} />
          </div>
          <div className="w-1/3">
            <Usage state={state} setState={setState} />
          </div>
        </div>

        { !!state.uid &&
          <div className='mt-2 space-y-2'>
            <RatesCalculator
              dealId={state.deal_id}
              orderId={state.uid}
              companyCurrency={state.companyCurrencyIso}
              setCallback={setCallback}
              saving={saving}
            />
            <Drafts
              orderTasks={state.tasks}
              orderId={state.uid}
              companyCurrency={state.companyCurrencyIso}
              setCallback={setCallback}
              saving={saving}
            />
          </div>
        }
        { showDistribution &&
          <Distribution state={state} setState={setState} />
        }

        <div className="flex justify-between mt-6">
          <Button
            variant="inline"
            onClick={() => setShowDistribution(!showDistribution)} 
            className="underline text-gray-700"
          >
            Toggle distribution details
          </Button>
        </div>
      </div>

      <div className="w-1/5 border-l-2 border-gray-300 shadow-inner bg-gray-100 max-h-full overflow-scroll">
        { state.uid &&
          <Files orderId={state.uid} />
        }
      </div>
    </div>
  )
}

export default EmbeddedDealPage
